import { Injectable, OnDestroy } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { BASE_SITE_CONTEXT_ID, StatePersistenceService, StateUtils } from '@spartacus/core';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilKeyChanged, filter, map } from 'rxjs/operators';
import { IndexedState } from '../../features/store/base-feature-state';
import { PrincipalConfiguration } from '../../model';
import { SiteContextParamsService } from '../../site-context/services/site-context-params.service';
import { UserStatePersistenceFacade } from '../facades';
import { UserActions } from '../store/actions';
import { UserSelectors } from '../store/selectors';
import { StateWithUser } from '../store/user-state';

@Injectable()
export class UserStatePersistenceService implements OnDestroy, UserStatePersistenceFacade {
  protected subscription = new Subscription();

  constructor(
    private statePersistenceService: StatePersistenceService,
    private store: Store<StateWithUser>,
    private siteContextParamsService: SiteContextParamsService
  ) {}

  public initSync() {
    this.subscription.add(
      this.statePersistenceService.syncWithStorage({
        key: 'user',
        state$: this.getUserState(),
        context$: this.siteContextParamsService.getValues([BASE_SITE_CONTEXT_ID]),
        onRead: (state) => this.onRead(state),
      })
    );
  }

  protected getUserState(): Observable<{ principalConfiguration: StateUtils.LoaderState<IndexedState<PrincipalConfiguration>> }> {
    return this.store.pipe(
      select(UserSelectors.getUserState),
      filter((state) => !!state),
      distinctUntilKeyChanged('principalConfiguration'),
      map((state) => {
        return {
          principalConfiguration: state.principalConfiguration,
        };
      })
    );
  }

  protected onRead(state: { principalConfiguration: StateUtils.LoaderState<IndexedState<PrincipalConfiguration>> } | undefined) {
    if (this.canBeRestored(state)) {
      const principalConfigurations = [];
      for (const principalConfiguration of Object.keys(state.principalConfiguration.value.entities)) {
        principalConfigurations.push(state.principalConfiguration.value.entities[principalConfiguration]);
      }
      this.store.dispatch(new UserActions.LoadPrincipalConfigurationValuesSuccess(principalConfigurations));
    }
  }

  private canBeRestored(state: { principalConfiguration: StateUtils.LoaderState<IndexedState<PrincipalConfiguration>> }) {
    return (
      state &&
      state.principalConfiguration &&
      state.principalConfiguration.value &&
      state.principalConfiguration.value.entities &&
      Object.keys(state.principalConfiguration.value?.entities).length !== 0
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
